import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ceederoApi } from "@/api/client";

import type { CatalogTrack, CloudLibrary, LibraryProject, LibraryShelf } from "@/cloud/types/types";

interface ChangePullPage {
  cursor: string;
  hasMore: boolean;
  projects: LibraryProject[];
  /** The Shelves that changed, each with the discs standing on it. */
  shelves: LibraryShelf[];
  storageLimitBytes: number;
  storedBytes: number;
  tracks: CatalogTrack[];
}

/**
 * A pull is bounded so a bug in the loop cannot become an unbounded request
 * storm against the user's own quota. A library large enough to reach this has
 * a paging interface waiting for it, not a higher number here.
 */
const MAXIMUM_PAGES = 50;

export const cloudLibraryQueryKey = ["cloud", "library"] as const;

/**
 * The whole Cloud Library — its projects, its Catalog Tracks and the Shelves it
 * is arranged on — read through the interface a Cloud Library is meant to be
 * read through.
 *
 * `GET /v1/library` and `GET /v1/library/catalog` would each answer half of
 * this in one request, and both are superseded: the index re-sends every live
 * project on every call and carries no tombstone, and the catalog's `since`
 * cursor steps over rows sharing a boundary millisecond. They are answered only
 * until the installed desktop stops calling them, and are to gain no new caller
 * (ADR-0003 in `ceedero-api`) — so this reads the change pull, from no cursor,
 * which is a full pull.
 *
 * Paged to exhaustion rather than incrementally: this is a management console
 * that shows a complete list and holds no cursor between visits. A partial
 * library drawn as though it were the whole one is the one outcome worth
 * avoiding, since nothing on screen would say a project was missing.
 */
export const useCloudLibrary = () =>
  useQuery({
    queryKey: cloudLibraryQueryKey,
    queryFn: async ({ signal }): Promise<CloudLibrary> => {
      const projects = new Map<string, LibraryProject>();
      const shelves = new Map<string, LibraryShelf>();
      const tracks = new Map<string, CatalogTrack>();
      let cursor: string | undefined;
      let storedBytes = 0;
      let storageLimitBytes = 0;

      for (let page = 0; page < MAXIMUM_PAGES; page += 1) {
        const result: ChangePullPage = await ceederoApi.request({
          path: `/v1/library/changes${cursor === undefined ? "" : `?cursor=${encodeURIComponent(cursor)}`}`,
          signal,
        });
        // Keyed by identity and overwritten, because a change feed may report
        // the same project twice and the later word is the true one.
        for (const project of result.projects) {
          projects.set(project.desktopProjectId, project);
        }
        for (const shelf of result.shelves) {
          shelves.set(shelf.id, shelf);
        }
        for (const track of result.tracks) {
          tracks.set(track.id, track);
        }
        storedBytes = result.storedBytes;
        storageLimitBytes = result.storageLimitBytes;
        cursor = result.cursor;
        if (!result.hasMore) {
          break;
        }
      }

      return {
        projects: [...projects.values()],
        shelves: [...shelves.values()],
        storageLimitBytes,
        storedBytes,
        tracks: [...tracks.values()],
      };
    },
  });

/**
 * Removing a project from the Cloud Library. It is removed from the cloud
 * alone: the copy on whichever computer published it is untouched, and this
 * application never reaches a document.
 */
export const useRemoveLibraryProject = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (desktopProjectId: string) =>
      ceederoApi.request<void>({
        method: "DELETE",
        path: `/v1/library/projects/${encodeURIComponent(desktopProjectId)}`,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: cloudLibraryQueryKey }),
  });
};
